import { useEffect, useRef, useState } from "react";
import { Backdrop, Box, Button, CircularProgress, Modal, TextField, Typography } from "@mui/material";
import { Email, SecurityOutlined, Sms } from "@mui/icons-material";
import { gateway } from "../gateway";
import { colors } from "./ui/colors";

export const ResetPassword = () => {
  const [step, setStep] = useState("method"); // "method" | "input" | "code" | "reset"
  const [method, setMethod] = useState(null); // "email" | "phone"
  const [destination, setDestination] = useState("");
  const [contact, setContact] = useState("");
  const [code, setCode] = useState(["", "", "", "", ""]);
  const [isCodeComplete, setIsCodeComplete] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    setShowContent(false);
    setTimeout(() => setShowContent(true), 200);
    setStep("method");
    setMethod(null);
    setContact("");
    setDestination("");
    setCode(["", "", "", "", ""]);
    setIsCodeComplete(false);
  }, []);

  const handleSendCode = async () => {
    if (!contact) return;
    setLoading(true);
    try {
      await gateway.post("/users/send-reset-password", {
        twoFaMethod: contact,
      });
      setDestination(contact);
      setStep("code");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...code];
    next[index] = value;
    setCode(next);
    setIsCodeComplete(next.every((d) => d.length === 1));
    if (value && index < code.length - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const password = data.get("password");
    const confirm = data.get("confirmPassword");
    if (password !== confirm) return;

    setLoading(true);
    try {
      await gateway.post("/users/reset-password", {
        [method]: contact,
        code: code.join(""),
        password,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "grid",
        placeItems: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 520,
          borderRadius: "16px",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          p: { xs: 4, md: 6 },
          textAlign: "center",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          border: "1px solid rgba(251, 142, 0, 0.12)",
          transform: "translateY(0)",
          opacity: 1,
          transition: "all 360ms ease-out",
        }}
      >
        <Box sx={{ display: "inline-flex", mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${colors.orange.base} 0%, ${colors.yellow.base} 100%)`,
              boxShadow: "0 8px 24px rgba(251,142,0,0.25)",
            }}
          >
            <SecurityOutlined sx={{ fontSize: 40, color: "white" }} />
          </Box>
        </Box>

        {/* STEP 1: Method */}
        {step === "method" && (
          <>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: colors.gray[800] }}>
              Reset Your Password
            </Typography>
            <Typography sx={{ color: colors.gray[600], mb: 3 }}>Choose how to receive your verification code</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4 }}>
              <Button
                onClick={() => {
                  setMethod("email");
                  setStep("input");
                }}
                fullWidth
              >
                <Email sx={{ mr: 2, color: colors.orange.base }} /> Email
              </Button>
              <Button
                onClick={() => {
                  setMethod("phone");
                  setStep("input");
                }}
                fullWidth
              >
                <Sms sx={{ mr: 2, color: colors.orange.base }} /> SMS
              </Button>
            </Box>
            <Button fullWidth variant="text" sx={{ color: colors.orange.base }}>
              Cancel
            </Button>
          </>
        )}

        {/* STEP 2: Input */}
        {step === "input" && (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Enter your {method}
            </Typography>
            <TextField
              fullWidth
              type={method === "email" ? "email" : "tel"}
              label={method === "email" ? "Email Address" : "Phone Number"}
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Button onClick={handleSendCode} disabled={loading} fullWidth variant="contained">
              {loading ? <CircularProgress size={24} /> : "Send Code"}
            </Button>
          </>
        )}

        {/* STEP 3: Code */}
        {step === "code" && (
          <>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
              Enter Verification Code
            </Typography>
            <Typography sx={{ color: colors.gray[600], mb: 3 }}>
              We sent a code to your {method}: <br /> <strong>{destination}</strong>
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
              {code.map((digit, i) => (
                <TextField
                  key={i}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  inputRef={(ref) => (inputsRef.current[i] = ref)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                    style: { textAlign: "center", fontSize: "20px", fontWeight: 600 },
                  }}
                  sx={{ width: 56 }}
                />
              ))}
            </Box>
            <Button onClick={() => setStep("reset")} disabled={!isCodeComplete} fullWidth variant="contained">
              Verify Code
            </Button>
          </>
        )}

        {/* STEP 4: Reset */}
        {step === "reset" && (
          <Box component="form" onSubmit={handleResetPassword}>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
              Set New Password
            </Typography>
            <TextField name="password" type="password" label="New Password" fullWidth required sx={{ mb: 2 }} />
            <TextField name="confirmPassword" type="password" label="Confirm Password" fullWidth required sx={{ mb: 3 }} />
            <Button type="submit" fullWidth variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Reset Password"}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
